import React, { useCallback, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Checkbox,
  Typography,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";

/**
 * DynamicTable - Excel-like table component with dynamic rows
 * @param {string} title - Table title (e.g., "INCOME", "EXPENSES")
 * @param {Array} columns - Column definitions: [{key, label, type, editable}]
 * @param {Array} data - Array of row objects
 * @param {Function} onChange - Callback when data changes
 * @param {string} headerColor - Background color for header
 * @param {boolean} showCheckbox - Show checkbox column
 * @param {boolean} showTotal - Show total row at bottom
 * @param {string} currency - Currency symbol to display (e.g., "$", "€")
 * @param {Function} onAddItem - Callback when adding a new item
 * @param {Function} onUpdateItem - Callback when updating an item
 * @param {Function} onDeleteItem - Callback when deleting an item
 */
const DynamicTable = React.memo(({
  title,
  columns = [],
  data = [],
  onChange,
  headerColor = "#7c88a2",
  showCheckbox = false,
  showTotal = true,
  minRows = 4,
  currency = "$",
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}) => {
  const theme = useTheme();

  const paddedData = useMemo(() => {
    const result = [...data];
    while (result.length < minRows) {
      result.push(createEmptyRow());
    }
    return result;
  }, [data, minRows]);

  function createEmptyRow() {
    // Generate UUID v4
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    
    const row = { id: uuid };
    columns.forEach(col => {
      if (col.type === 'number') {
        row[col.key] = '';
      } else if (col.type === 'checkbox') {
        row[col.key] = false;
      } else if (col.type === 'progress') {
        row[col.key] = 0;
      } else {
        row[col.key] = '';
      }
    });
    return row;
  }

  const handleCellChange = useCallback((rowIndex, columnKey, value) => {
    const newData = [...paddedData];
    const currentRow = newData[rowIndex];
    const updatedRow = { ...currentRow, [columnKey]: value };
    newData[rowIndex] = updatedRow;

    // Check if this is an existing item with an id
    if (currentRow.id && onUpdateItem) {
      // Check if row has enough data to be considered valid
      const hasValidData = columns.some(col => {
        const val = updatedRow[col.key];
        if (col.type === 'number') {
          return val && parseFloat(val) !== 0;
        }
        return val && val !== '';
      });

      if (hasValidData) {
        // Check if this is a new item (hasn't been created on backend yet)
        const isNewItem = !currentRow.createdOn;
        
        if (isNewItem) {
          // Check if both name and amount are filled
          const hasName = updatedRow.name && updatedRow.name.trim() !== '';
          const hasAmount = updatedRow.amount && parseFloat(updatedRow.amount) > 0;
          
          if (hasName && hasAmount && onAddItem) {
            // Both fields are filled - call onAddItem to create on backend
            onAddItem(currentRow.id, updatedRow.name.trim(), parseFloat(updatedRow.amount));
          }
        } else if (!isNewItem) {
          // This is an existing item - call update API
          onUpdateItem(currentRow.id, updatedRow.name || '', parseFloat(updatedRow.amount) || 0);
        }
      }
    }

    const nonEmptyData = newData.filter(row => {
      return columns.some(col => {
        if (col.type === 'number') {
          return row[col.key] && parseFloat(row[col.key]) !== 0;
        }
        return row[col.key] && row[col.key] !== '';
      });
    });

    onChange(nonEmptyData);
  }, [paddedData, columns, onChange, onUpdateItem, onAddItem]);

  const handleAddRow = useCallback(() => {
    const newRow = createEmptyRow();
    
    // Don't call the add API callback yet - wait until user enters amount
    // The API call will be made in handleCellChange when amount is entered
    
    const newData = [...data, newRow];
    onChange(newData);
  }, [data, onChange]);

  const handleDeleteRow = useCallback((rowIndex) => {
    const rowToDelete = paddedData[rowIndex];
    
    // Call delete API if row has an id
    if (rowToDelete.id && onDeleteItem) {
      onDeleteItem(rowToDelete.id);
    }
    
    // Always allow deletion, even if empty
    const newData = paddedData.filter((_, index) => index !== rowIndex);
    
    // Filter out completely empty rows
    const cleanedData = newData.filter(row => 
      columns.some(col => {
        const value = row[col.key];
        if (col.type === 'number') {
          return value && parseFloat(value) !== 0;
        }
        return value && value !== '';
      })
    );
    
    onChange(cleanedData);
  }, [paddedData, onChange, columns, onDeleteItem]);

  const calculateColumnTotal = useCallback((columnKey) => {
    return paddedData.reduce((sum, row) => {
      const value = parseFloat(row[columnKey]) || 0;
      return sum + value;
    }, 0);
  }, [paddedData]);

  const formatCurrency = (value) => {
    return `${currency} ${parseFloat(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercent = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  const totalAmount = useMemo(() => calculateColumnTotal('amount'), [calculateColumnTotal]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h6"
        sx={{
          backgroundColor: headerColor,
          p: 1.5,
          textAlign: 'center',
          fontWeight: 'bold',
          borderTopLeftRadius: 1,
          borderTopRightRadius: 1,
        }}
      >
        {title}
      </Typography>
      <TableContainer
        sx={{
          border: `1px solid #d0d0d0`,
          borderRadius: 0,
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
          <TableBody>
            {paddedData.map((row, rowIndex) => (
              <TableRow 
                key={row.id || rowIndex} 
                hover
                sx={{
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                {showCheckbox && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={row.checked || false}
                      onChange={(e) => handleCellChange(rowIndex, 'checked', e.target.checked)}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell 
                    key={col.key} 
                    align={col.type === 'number' ? 'center' : 'left'}
                    sx={{ 
                      padding: '4px 8px',
                      borderRight: '1px solid #e0e0e0',
                      backgroundColor: 'white',
                      '&:last-child': { borderRight: 'none' },
                    }}
                  >
                    {col.editable !== false ? (
                      col.type === 'number' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.875rem',
                              color: '#666',
                              mr: 0.5,
                            }}
                          >
                            {currency}
                          </Typography>
                          <TextField
                            type="text"
                            size="small"
                            value={row[col.key] || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handleCellChange(rowIndex, col.key, value);
                              }
                            }}
                            placeholder=""
                            sx={{
                              flex: 1,
                              '& .MuiInputBase-input': {
                                textAlign: 'right',
                                py: 0.5,
                                px: 1,
                                fontSize: '0.875rem',
                                cursor: 'text',
                                backgroundColor: 'white',
                              },
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { 
                                  border: 'none',
                                },
                                '&:hover': {
                                  backgroundColor: '#f0f8ff',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'white',
                                  boxShadow: 'inset 0 0 0 2px #4285f4',
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          value={row[col.key] || ''}
                          onChange={(e) => handleCellChange(rowIndex, col.key, e.target.value)}
                          placeholder=""
                          sx={{
                            width: '100%',
                            '& .MuiInputBase-input': {
                              py: 0.5,
                              px: 1,
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'text',
                              backgroundColor: 'white',
                            },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { 
                                border: 'none',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f8ff',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                                boxShadow: 'inset 0 0 0 2px #4285f4',
                              },
                            },
                          }}
                        />
                      )
                    ) : (
                      <Typography variant="body2">
                        {col.type === 'number' ? formatCurrency(row[col.key]) : row[col.key]}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ padding: '4px 8px', textAlign: 'center', borderRight: '1px solid #e0e0e0', backgroundColor: 'white' }}>
                  <Tooltip title="Delete row" arrow placement="left">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow(rowIndex)}
                      sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {showTotal && ((
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', borderTop: '2px solid #d0d0d0' }}>
                {showCheckbox && <TableCell sx={{ padding: '4px 8px', borderRight: '1px solid #e0e0e0' }}></TableCell>}
                <TableCell sx={{ fontWeight: 'bold', padding: '8px', borderRight: '1px solid #e0e0e0' }}>TOTAL</TableCell>
                {columns.slice(1).map((col) => (
                  <TableCell 
                    key={col.key} 
                    align={col.type === 'number' ? 'center' : 'left'}
                    sx={{ padding: '8px', borderRight: '1px solid #e0e0e0', '&:last-child': { borderRight: 'none' } }}
                  >
                    {col.type === 'number' && col.key === 'amount' && (
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(totalAmount)}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ padding: '8px' }}></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
        <Tooltip title={`Add ${title.toLowerCase()} item`} arrow placement="top">
          <IconButton
            size="small"
            onClick={handleAddRow}
            sx={{
              backgroundColor: headerColor,
              '&:hover': { backgroundColor: headerColor, opacity: 0.8 },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

export default DynamicTable;
